import {freeRect, shrink, paddingOf} from './safe-frame.mjs';
import {pinScale} from './pins.mjs';

// Controls that float over the map. Measured, not assumed: the layout modes, the chip row and the result chip decide where they sit.
// A node marked data-pending is laid out but not yet shown (the result chip waits for its pins); it counts all the same.
export const OBSTACLES = ['#place-search', '#menu-button', '#quick-chips', '#hits-chip', '.map-fabs', '#map-brand',
  '.maplibregl-ctrl-top-right', '.maplibregl-ctrl-bottom-left', '.maplibregl-ctrl-bottom-right'];
// Screen room a pin needs around its anchor (the tip): the head above, the badge and the query label below, the rank tile to the left.
export function pinMargin(scale = pinScale()) {
  return {top: Math.ceil(51 * scale) + 8, bottom: Math.ceil(25 * scale) + 12, left: 29 + 14 + 8, right: Math.ceil(21 * scale) + 8};
}
function shown(node, win) {
  if (node.hasAttribute?.('data-pending')) return true;
  const style = win.getComputedStyle?.(node);
  return !style || (style.visibility !== 'hidden' && style.display !== 'none' && style.opacity !== '0');
}
// Safe-area insets (notch, home indicator) through a probe element; 0 without env() support.
function insets(doc, win) {
  const probe = doc.createElement('div');
  probe.style.cssText = 'position:fixed;visibility:hidden;pointer-events:none;inset:0;padding:env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)';
  doc.body.append(probe);
  const style = win.getComputedStyle(probe), px = v => Number.parseFloat(v) || 0;
  const out = {top: px(style.paddingTop), right: px(style.paddingRight), bottom: px(style.paddingBottom), left: px(style.paddingLeft)};
  probe.remove();
  return out;
}
// The area of the map that holds a search: {size, free, inner, padding}. `panelLeft` is the width an open side panel takes from the left.
export function frameView(map, {doc = document, win = globalThis, panelLeft = 0, selectors = OBSTACLES} = {}) {
  const element = map.getContainer(), box = element.getBoundingClientRect();
  const size = {width: element.clientWidth, height: element.clientHeight}, obstacles = [];
  for (const node of doc.querySelectorAll(selectors.join(','))) {
    const r = node.getBoundingClientRect();
    if (!(r.width > 0 && r.height > 0) || !shown(node, win)) continue;
    obstacles.push({left: r.left - box.left, top: r.top - box.top, right: r.right - box.left, bottom: r.bottom - box.top});
  }
  if (panelLeft > 0) obstacles.push({left: 0, top: 0, right: panelLeft, bottom: size.height});
  // The inset only reaches the map where it touches the screen edge.
  const raw = insets(doc, win), edge = {left: box.left, top: box.top, right: (win.innerWidth ?? box.right) - box.right, bottom: (win.innerHeight ?? box.bottom) - box.bottom};
  const inset = Object.fromEntries(Object.keys(raw).map(side => [side, Math.max(0, raw[side] - Math.max(0, edge[side]))]));
  const free = freeRect(size, obstacles, inset), inner = shrink(free, pinMargin());
  return {size, free, inner: inner.right > inner.left && inner.bottom > inner.top ? inner : free, padding: paddingOf(size, inner)};
}
